import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';

const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;

const EmailOtp = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(60); // Timer in seconds
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const navigate = useNavigate();

  // Timer countdown
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setIsResendDisabled(false); // Enable the resend button when timer reaches 0
    }
  }, [timer]);

  const handleChange = (value, index) => {
    if (isNaN(value)) return; // Ignore non-numeric input

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value !== '' && index < 5) {
      document.getElementById(`otp-input-${index + 1}`).focus(); // Move to the next input
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];
      if (otp[index] === '') {
        if (index > 0) {
          document.getElementById(`otp-input-${index - 1}`).focus(); // Move to the previous input
        }
      } else {
        newOtp[index] = ''; // Clear the current input
        setOtp(newOtp);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const enteredOtp = otp.join('');

    if (otp.some((digit) => digit === '')) {
      toast.error('Please fill all the OTP boxes.');
      return;
    }

    // Retrieve email from localStorage
    const email = localStorage.getItem('email');

    // Check if email is missing from localStorage
    if (!email) {
      toast.error('Email is missing. Please check your email and try again.');
      return;
    }

    console.log('Email retrieved from localStorage:', email); // Debugging line

    try {
      // API call to verify OTP
      const response = await fetch(`${AQ_BASE_URL}/aq/api/emailVerifyOTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({
          verification_code: enteredOtp,
          email: email, // Ensure email is included
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(data.message); // Display success message
        navigate('/password-reset'); // Navigate to reset password page
      } else {
        toast.error(data.detail || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred. Please try again later.');
    }
  };

  const handleResend = async () => {
    const email = localStorage.getItem('email');

    // Check if email is missing from localStorage
    if (!email) {
      toast.error('Email is missing. Please check your email and try again.');
      return;
    }

    console.log('Email retrieved from localStorage:', email); // Debugging line

    try {
      const response = await fetch(`${AQ_BASE_URL}/aq/api/resendOTP`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          email: email,
        },
      });

      if (response.ok) {
        const data = await response.json();
        toast.info(data.detail || 'A new OTP has been sent to your email.');
        setOtp(['', '', '', '', '', '']); // Reset the OTP inputs
        setTimer(60); // Reset the timer
        setIsResendDisabled(true); // Disable resend button until timer ends
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      console.error('Error while resending OTP:', error);
      toast.error('An error occurred while resending OTP. Please try again.');
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
      <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full">
        <h1 className="text-2xl font-bold text-center">E-mail Verification</h1>
        <p className="text-xs text-slate-500 mb-5 text-center">
          Enter the 6-digit verification code that was sent to your E-mail address.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-center gap-3">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-input-${index}`}
                type="text"
                value={digit}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className="w-10 h-10 text-center text-base font-extrabold text-slate-900 bg-slate-100 border appearance-none rounded p-3 outline-none focus:bg-white focus:border-green-800 focus:ring-2 focus:ring-green-200"
                maxLength="1"
              />
            ))}
          </div>

          <button
            type="submit"
            className="w-full inline-flex justify-center mt-4 rounded-lg bg-green-700 px-3.5 py-2.5 text-sm font-medium text-white shadow-sm shadow-indigo-950/10 hover:bg-green-800"
          >
            Verify Account
          </button>
        </form>

        <div className="text-center text-sm text-slate-500 mt-4">
          {timer > 0 ? (
            <div className="flex justify-center">
              <h1 className="text-black">Resend code in</h1>
              <p className="text-sm text-red-500 ml-1">{formatTime(timer)}</p>
            </div>
          ) : (
            <div className="flex justify-center gap-1">
              <h1 className="text-black">Didn't receive code?</h1>
              <button
                className={`font-medium ml-1 text-green-600 hover:text-green-700 ${
                  isResendDisabled ? 'cursor-not-allowed' : ''
                }`}
                onClick={handleResend}
                disabled={isResendDisabled}
              >
                Resend
              </button>
            </div>
          )}
        </div>
      </div>
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
    </div>
  );
};

export default EmailOtp;
